import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse, IAuthFlow } from '@core/models';
import { Observable } from 'rxjs';

interface PasswordSessionOptions {
  completeAuthorizationCode?: boolean;
}

export interface PendingAuthorization {
  authorization_code?: string | null;
  authorization_request?: string | null;
}

export interface OAuthCodeSessionRequest {
  clientId: string;
  code: string;
  codeVerifier: string;
  redirectUri: string;
  authorization?: PendingAuthorization | null;
  expectedNonce?: string | null;
  identityProvider?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class BrokerTokenStorageService {
  private sessionHttp: HttpClient;

  constructor(httpBackend: HttpBackend) {
    this.sessionHttp = new HttpClient(httpBackend);
  }

  createSession(flow: IAuthFlow): Observable<IApiResponse> {
    return this.completeAuthorizationCode(flow);
  }

  completeAuthorizationCode(flow: IAuthFlow): Observable<IApiResponse> {
    const session = {
      action: 'complete_authorization_code',
      client_id: flow.client_id,
      ...this.authorizationFields(flow)
    };

    return this.sessionHttp.post<IApiResponse>('/session', session, { withCredentials: true });
  }

  currentSession(clientId: string): Observable<IApiResponse> {
    return this.sessionHttp.post<IApiResponse>(
      '/session',
      {
        action: 'current',
        client_id: clientId
      },
      { withCredentials: true }
    );
  }

  createOAuthCodeSession(request: OAuthCodeSessionRequest): Observable<IApiResponse> {
    const session: Record<string, string | null | undefined> = {
      action: 'oauth_code',
      client_id: request.clientId,
      code: request.code,
      code_verifier: request.codeVerifier,
      redirect_uri: request.redirectUri,
      expected_nonce: request.expectedNonce,
      identity_provider: request.identityProvider
    };

    Object.assign(session, this.authorizationFields(request.authorization));

    return this.sessionHttp.post<IApiResponse>('/session', session, { withCredentials: true });
  }

  createPasswordSession(
    flow: IAuthFlow,
    username: string,
    password: string,
    options: PasswordSessionOptions = {}
  ): Observable<IApiResponse> {
    const session: Record<string, string | null | undefined> = {
      action: 'password',
      client_id: flow.client_id,
      username,
      password
    };

    if (options.completeAuthorizationCode) {
      Object.assign(session, this.authorizationFields(flow));
    }

    return this.sessionHttp.post<IApiResponse>('/session', session, { withCredentials: true });
  }

  createChallengeSession(
    clientId: string,
    response: string,
    authorization?: PendingAuthorization | null
  ): Observable<IApiResponse> {
    return this.sessionHttp.post<IApiResponse>(
      '/session',
      {
        action: 'challenge',
        client_id: clientId,
        response,
        ...this.authorizationFields(authorization)
      },
      { withCredentials: true }
    );
  }

  startAccountSecurityStepUp(
    clientId: string,
    username: string,
    password: string,
    operation: string
  ): Observable<IApiResponse> {
    return this.sessionHttp.post<IApiResponse>(
      '/session',
      {
        action: 'password',
        client_id: clientId,
        username,
        password,
        step_up_operation: operation
      },
      { withCredentials: true }
    );
  }

  completeAccountSecurityStepUp(
    clientId: string,
    code: string,
    operation: string
  ): Observable<IApiResponse> {
    return this.sessionHttp.post<IApiResponse>(
      '/session',
      {
        action: 'challenge',
        client_id: clientId,
        response: code,
        step_up_operation: operation
      },
      { withCredentials: true }
    );
  }

  private authorizationFields(
    authorization: PendingAuthorization | null | undefined
  ): Record<string, string> {
    if (authorization?.authorization_request) {
      return { authorization_request: authorization.authorization_request };
    }

    if (authorization?.authorization_code) {
      return { authorization_code: authorization.authorization_code };
    }

    return {};
  }
}
